/**
 * Сравнение двух документов JSON по значениям, а не по тексту.
 *
 * Текстовый diff здесь бесполезен: переставленные ключи и разные отступы
 * дают горы «различий» там, где данные одинаковы. Человеку нужен ответ на
 * вопрос «что поменялось в данных», поэтому обход идёт по структуре.
 */

export type JsonDiffKind = 'added' | 'removed' | 'changed';

export interface JsonDiffEntry {
  /** Путь вида `items[2].name`; пустая строка означает корень. */
  path: string;
  kind: JsonDiffKind;
  left?: unknown;
  right?: unknown;
}

/** Короткая запись значения для строки списка различий. */
export function shortValue(value: unknown): string {
  if (value === undefined) return '—';
  if (value === null) return 'null';
  if (Array.isArray(value)) return `[${String(value.length)}]`;
  if (typeof value === 'object') return `{${String(Object.keys(value).length)}}`;

  // Здесь остаются только простые значения: объекты, массивы и null
  // отсеяны выше, поэтому строка всегда получается.
  const text = JSON.stringify(value);
  return text.length > 40 ? `${text.slice(0, 39)}…` : text;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function join(path: string, key: string): string {
  return path ? `${path}.${key}` : key;
}

/**
 * Различия между двумя разобранными значениями.
 *
 * Список ограничен сверху: на документах в тысячи расхождений он всё равно
 * не читается, а страница от него встаёт.
 */
export function diffJson(left: unknown, right: unknown, limit = 500): JsonDiffEntry[] {
  const out: JsonDiffEntry[] = [];

  const walk = (a: unknown, b: unknown, path: string): void => {
    if (out.length >= limit) return;

    if (Object.is(a, b)) return;

    if (isPlainObject(a) && isPlainObject(b)) {
      for (const key of new Set([...Object.keys(a), ...Object.keys(b)])) {
        const inA = key in a;
        const inB = key in b;

        if (!inB) out.push({ path: join(path, key), kind: 'removed', left: a[key] });
        else if (!inA) out.push({ path: join(path, key), kind: 'added', right: b[key] });
        else walk(a[key], b[key], join(path, key));
      }
      return;
    }

    if (Array.isArray(a) && Array.isArray(b)) {
      const max = Math.max(a.length, b.length);
      for (let i = 0; i < max; i += 1) {
        const item = `${path}[${String(i)}]`;
        if (i >= b.length) out.push({ path: item, kind: 'removed', left: a[i] });
        else if (i >= a.length) out.push({ path: item, kind: 'added', right: b[i] });
        else walk(a[i], b[i], item);
      }
      return;
    }

    if (JSON.stringify(a) === JSON.stringify(b)) return;

    out.push({ path, kind: 'changed', left: a, right: b });
  };

  walk(left, right, '');
  return out.slice(0, limit);
}

export type JsonParseResult =
  | { ok: true; value: unknown }
  | { ok: false; message: string; line: number | null };

/** Разбор JSON с указанием строки: искать позицию символом невозможно. */
export function parseJson(text: string): JsonParseResult {
  if (!text.trim()) return { ok: true, value: undefined };

  try {
    return { ok: true, value: JSON.parse(text) };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    const at = /position (\d+)/.exec(message);
    const line = at?.[1] ? text.slice(0, Number(at[1])).split('\n').length : null;

    return { ok: false, message, line };
  }
}
